import { toCellId } from '@brig/rules-engine';
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, type IconName } from '../game/icons.tsx';
import * as css from './Board.css.ts';
import { MAX_CELL_PX, useCellPx } from './boardCamera.ts';
import type { Camera } from './camera.ts';
import {
  CELL,
  cellAt,
  cellCenter,
  pickInView,
  shipArtAt,
  shipsByCell,
  worldSize,
  type Hit,
} from './geometry.ts';
import { attachInput } from './input.ts';
import { Minimap } from './Minimap.tsx';
import { cellScreen, keepOnScreen, markUserInput } from './focus.ts';
import { projection, toView, toWorld, viewBounds, type ProjectionKind } from './projection.ts';
import { CHART_REACH, type MapStyle } from './chartStyle.ts';
import type { BoardScene, SceneProps } from './scene.ts';

export interface BoardViewProps extends SceneProps {
  readonly camera: Camera;
  /** How the board is seen; isometric unless told otherwise. */
  readonly projection?: ProjectionKind;
  /** How the map is drawn; the engraved chart unless told otherwise. */
  readonly mapStyle?: MapStyle;
  readonly onCell: (cell: string) => void;
  readonly onUnit: (id: string, shift: boolean) => void;
  /** The cell to keep in place when the projection changes (U-VIEW-05), e.g. the selection's. */
  readonly focus?: string | null;
  /** A long press or right click on a unit: show it, act on nothing (U-TOUCH-03). */
  readonly onUnitHold?: (id: string) => void;
  /**
   * A tap or click on a cell where two ships stand: let the player pick (U-SEL-02). `at` is
   * the point in the board's CSS px, to open the picker next to it.
   */
  readonly onPickShared?: (cell: string, ids: readonly string[], at: { x: number; y: number }) => void;
  /** Show the minimap (the page may fold it away on small screens). */
  readonly minimap?: boolean;
  /** Fold the minimap away or bring it back: a button with the zoom ones and × on the minimap. */
  readonly onToggleMinimap?: () => void;
  /**
   * A unit's tooltip next to the mouse pointer while it rests on the unit: what it is, whose,
   * its damage. It never takes the pointer, so the click still reaches the board.
   */
  readonly unitTip?: (id: string) => ReactNode;
}

type Status = 'loading' | 'ready' | 'unsupported';

/** Half a 44 px touch target: how far from a unit a finger may land (U-TOUCH-02). */
const TOUCH_RADIUS_PX = 22;

/**
 * The game board: a PixiJS scene behind a camera (wheel / pinch to zoom, drag to pan), with a
 * minimap and zoom buttons. The scene and Pixi itself load lazily; without WebGL (or in tests)
 * the board says so and the rest of the page keeps working.
 */
export function BoardView(outer: BoardViewProps) {
  const { t } = useTranslation();
  const signs = useMemo(
    () => ({
      fort: t('game.chart.fort'),
      royalPort: t('game.chart.royalPort'),
    }),
    [t],
  );
  const props = { ...outer, signs };
  const { camera, state } = props;
  const proj = useMemo(() => projection(props.projection ?? 'iso'), [props.projection]);
  const hostRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<BoardScene | null>(null);
  const [status, setStatus] = useState<Status>(() => (webglAvailable() ? 'loading' : 'unsupported'));
  /** Why the board could not start, when it was not for want of WebGL. */
  const [failure, setFailure] = useState<string | null>(null);
  // The unit tooltip keeps its own state: following the mouse must not redraw the scene.
  const tipRef = useRef<(tip: TipAt | null) => void>(ignore);
  const setTipAt = (tip: TipAt | null) => {
    tipRef.current(tip);
  };
  const [hostWidth, setHostWidth] = useState(0);
  const cellPx = useCellPx(camera);

  // Handlers read the latest props without re-attaching the input.
  const latest = useRef(props);
  const ships = useMemo(() => shipsByCell(state.ships), [state.ships]);
  const shipsRef = useRef(ships);
  const projRef = useRef(proj);
  const sizeRef = useRef<string | null>(null);
  const mapStyle = props.mapStyle ?? 'chart';
  const styleRef = useRef(mapStyle);
  useLayoutEffect(() => {
    latest.current = props;
    shipsRef.current = ships;
  });

  // The camera looks at the projected map. Switching projection keeps the same spot of the
  // map at the centre of the screen and the zoom, or the whole map if that was on screen.
  const { width: worldW, height: worldH } = worldSize(state.map);
  useLayoutEffect(() => {
    const before = projRef.current;
    const v = camera.getSnapshot();
    const centre = toWorld(before, v.x, v.y);
    const wholeMap = v.zoom <= camera.minZoom * 1.001;
    const focus = latest.current.focus ?? null;
    const focusAt = focus ? cellScreen(camera, before.kind, focus) : null;
    projRef.current = proj;
    // The chart reaches past the grid: its strip and lettering are part of the whole map.
    const m = mapStyle === 'chart' ? CHART_REACH * CELL : 0;
    const box = viewBounds(proj, worldW + 2 * m, worldH + 2 * m);
    const corner = toView(proj, -m, -m);
    camera.setBounds({ ...box, x: box.x + corner.x, y: box.y + corner.y });
    // A new game on a map of another size (R-SIZE-01): show the whole of it.
    const size = `${worldW}x${worldH}`;
    const resized = sizeRef.current !== null && sizeRef.current !== size;
    sizeRef.current = size;
    if (resized) camera.fit();
    else if (before.kind !== proj.kind) {
      if (wholeMap) camera.fit();
      else if (focus && focusAt) keepOnScreen(camera, proj.kind, focus, focusAt);
      else {
        const next = toView(proj, centre.x, centre.y);
        camera.centerOn(next.x, next.y);
      }
    }
    sceneRef.current?.setProjection(proj);
  }, [camera, proj, worldW, worldH, mapStyle]);

  useEffect(() => {
    styleRef.current = mapStyle;
    sceneRef.current?.setMapStyle(mapStyle);
  }, [mapStyle]);

  // Viewport size, scene creation and teardown.
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const size = () => {
      camera.setViewport(host.clientWidth, host.clientHeight);
      setHostWidth(host.clientWidth);
      sceneRef.current?.resize(host.clientWidth, host.clientHeight);
    };
    size();
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(size);
    ro?.observe(host);
    if (status === 'unsupported') return () => ro?.disconnect();

    let cancelled = false;
    void import('./scene.ts')
      .then(({ BoardScene }) =>
        BoardScene.create(host, camera).catch((err: unknown) => {
          console.warn('board: WebGL scene failed to start', err);
          if (!cancelled) {
            setStatus('unsupported');
            setFailure(err instanceof Error ? err.message : String(err));
          }
          return null;
        }),
      )
      .then((scene) => {
        if (!scene) return;
        if (cancelled) {
          scene.destroy();
          return;
        }
        sceneRef.current = scene;
        try {
          scene.setProjection(projRef.current);
          scene.setMapStyle(styleRef.current);
          scene.setProps(latest.current);
          size();
          scene.syncCamera();
          setStatus('ready');
        } catch (err: unknown) {
          // The renderer works; drawing this position did not. Say so rather than blame WebGL.
          console.error('board: drawing the position failed', err);
          setFailure(err instanceof Error ? err.message : String(err));
          setStatus('ready');
        }
      })
      .catch((err: unknown) => {
        console.warn('board: the scene module failed to load', err);
        if (!cancelled) setStatus('unsupported');
      });
    return () => {
      cancelled = true;
      ro?.disconnect();
      sceneRef.current?.destroy();
      sceneRef.current = null;
    };
    // `status` only matters for the first run (unsupported from the start).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camera]);

  useEffect(() => {
    sceneRef.current?.setProps(props);
  });

  // Dev builds only: where a cell is on screen, for end-to-end scripts that click the board.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const w = window as unknown as { __brigCellScreen?: (cell: string) => { x: number; y: number } | null };
    w.__brigCellScreen = (cell) => {
      const r = hostRef.current?.getBoundingClientRect();
      const p = cellScreen(camera, projRef.current.kind, cell);
      return r ? { x: r.left + p.x, y: r.top + p.y } : null;
    };
    return () => {
      delete w.__brigCellScreen;
    };
  }, [camera]);

  // Pointer, wheel and keyboard.
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const hitAt = (sx: number, sy: number) => {
      const p = latest.current;
      const v = camera.screenToWorld(sx, sy);
      const w = toWorld(projRef.current, v.x, v.y);
      const cell = cellAt(p.state.map, w.x, w.y);
      const solid = (id: string, u: number, t: number) => sceneRef.current?.solidAt(id, u, t) ?? true;
      const input = { ...p, ships: shipsRef.current, solid };
      const hit = pickInView(input, projRef.current, v.x, v.y);
      // The ship whose artwork is under the point: one of a pair, for sure (U-SEL-02).
      const art = shipArtAt(input, projRef.current, v.x, v.y)?.id ?? null;
      return { cell: cell ? toCellId(cell) : null, hit, art };
    };
    /** The unit whose cell centre is within a fingertip of the point, if any. */
    const nearestUnit = (sx: number, sy: number): string | null => {
      const p = latest.current;
      let best: { id: string; d: number } | null = null;
      const consider = (id: string, cell: string) => {
        const c = cellCenter(cell);
        const v = toView(projRef.current, c.x, c.y);
        const s = camera.worldToScreen(v.x, v.y);
        const d = Math.hypot(s.x - sx, s.y - sy);
        if (d <= TOUCH_RADIUS_PX && (!best || d < best.d)) best = { id, d };
      };
      for (const ship of p.state.ships) if (!ship.sunk) consider(ship.id, ship.cell);
      for (const fort of p.state.forts) consider(fort.id, fort.cell);
      return (best as { id: string } | null)?.id ?? null;
    };
    let dragging = false;
    const cursor = (hit: Hit | null) => {
      const p = latest.current;
      if (dragging) return 'grabbing';
      if (!hit) return 'default';
      if (hit.kind === 'unit')
        return p.targets.has(hit.id) || p.volleyTarget === hit.id || p.attackersOf?.(hit.id).length
          ? 'crosshair'
          : 'pointer';
      return p.moveCells.has(hit.id) || p.portCells.has(hit.id) ? 'pointer' : 'default';
    };
    // U-VIEW-07: the camera stops following the action while the player moves the map.
    const touched = () => {
      markUserInput(camera);
    };
    host.addEventListener('wheel', touched, { passive: true });
    host.addEventListener('pointerdown', touched);
    // A finger has no hover: a tap on a unit shows its tooltip for a moment instead.
    let tapTip: ReturnType<typeof setTimeout> | undefined;
    const hideTip = () => {
      clearTimeout(tapTip);
      tapTip = undefined;
      setTipAt(null);
    };
    const detach = attachInput(host, camera, {
      onTap: (sx, sy, mods) => {
        const p = latest.current;
        const touch = mods.pointer !== 'mouse';
        const { cell, hit, art } = hitAt(sx, sy);
        // U-TOUCH-02: a fingertip is ~44 px; near a small unit counts as on it.
        const unit = hit?.kind === 'unit' ? hit.id : touch ? nearestUnit(sx, sy) : null;
        const unitCell = unit ? (p.state.ships.find((s) => s.id === unit)?.cell ?? null) : null;
        const pair = unitCell ? (shipsRef.current.get(unitCell) ?? []) : [];
        // U-SEL-02: the mouse on one ship's own artwork means that ship, even of a pair; a
        // fingertip, or a click between the two, leaves it to the picker.
        const exact = !touch && art !== null && art === unit;
        hideTip();
        if (unit && pair.length > 1 && p.onPickShared && unitCell && !exact) {
          p.onPickShared(
            unitCell,
            pair.map((s) => s.id),
            { x: sx, y: sy },
          );
        } else if (unit) {
          p.onUnit(unit, mods.shiftKey);
          if (touch && p.unitTip) {
            setTipAt({ id: unit, x: sx, y: sy, above: true });
            tapTip = setTimeout(hideTip, TAP_TIP_MS);
          }
        } else if (hit) p.onCell(hit.id);
        else if (cell) p.onCell(cell);
        host.focus({ preventScroll: true });
      },
      onHold: (sx, sy) => {
        hideTip();
        const { hit } = hitAt(sx, sy);
        const unit = hit?.kind === 'unit' ? hit.id : nearestUnit(sx, sy);
        if (unit) latest.current.onUnitHold?.(unit);
      },
      onHover: (sx, sy) => {
        const { cell, hit } = hitAt(sx, sy);
        host.style.cursor = cursor(hit);
        sceneRef.current?.setHover(cell);
        sceneRef.current?.setHoverShip(hit?.kind === 'unit' ? hit.id : null);
        setTipAt(hit?.kind === 'unit' && latest.current.unitTip ? { id: hit.id, x: sx, y: sy } : null);
      },
      onLeave: () => {
        // A lifted finger leaves the board too: that must not take the tap's tooltip away.
        if (tapTip === undefined) setTipAt(null);
        sceneRef.current?.setHover(null);
        sceneRef.current?.setHoverShip(null);
      },
      onDragChange: (v) => {
        if (v) hideTip();
        dragging = v;
        host.style.cursor = v ? 'grabbing' : 'default';
      },
    });
    return () => {
      clearTimeout(tapTip);
      detach();
      host.removeEventListener('wheel', touched);
      host.removeEventListener('pointerdown', touched);
    };
  }, [camera]);

  const minZoomPx = CELL * camera.minZoom;
  return (
    <div className={css.root} data-testid="board">
      <div
        ref={hostRef}
        className={css.viewport}
        tabIndex={0}
        role="application"
        aria-label="Game board"
        aria-describedby="board-help"
      />
      <span id="board-help" hidden>
        Scroll or pinch to zoom, drag to pan; arrow keys pan, plus and minus zoom, 0 shows the whole map.
      </span>
      {status === 'unsupported' ? (
        <div className={css.notice} role="alert">
          {failure && /dynamically imported module|Importing a module script failed/i.test(failure)
            ? 'The page is out of date (the dev server rebuilt its modules): reload it.'
            : 'The board needs WebGL: turn on hardware acceleration in the browser’s settings and reload.'}
          {failure ? ` (${failure})` : ''}
        </div>
      ) : null}
      {failure && status !== 'unsupported' ? (
        <div className={css.notice} role="alert">
          The board failed to draw: {failure}
        </div>
      ) : null}
      <div className={css.controls} role="group" aria-label={t('game.view.tools')}>
        <MapButton
          icon="plus"
          label={t('game.board.zoomIn')}
          keyHint="+"
          disabled={cellPx >= MAX_CELL_PX - 0.01}
          onClick={() => {
            camera.zoomBy(1.4);
          }}
        />
        <MapButton
          icon="minus"
          label={t('game.board.zoomOut')}
          keyHint="−"
          disabled={cellPx <= minZoomPx + 0.01}
          onClick={() => {
            camera.zoomBy(1 / 1.4);
          }}
        />
        <MapButton
          icon="fit"
          label={t('game.board.fit')}
          keyHint="0"
          onClick={() => {
            camera.fit(true);
          }}
        />
        {props.onToggleMinimap && status !== 'unsupported' ? (
          <MapButton
            icon="map"
            label={props.minimap === false ? t('game.board.minimapShow') : t('game.board.minimapHide')}
            pressed={props.minimap !== false}
            onClick={props.onToggleMinimap}
          />
        ) : null}
      </div>
      {props.unitTip ? (
        <UnitTip
          render={props.unitTip}
          register={(set) => {
            tipRef.current = set;
          }}
        />
      ) : null}
      {status === 'unsupported' || props.minimap === false ? null : (
        <div className={css.minimapFrame}>
          {props.onToggleMinimap ? (
            <button
              type="button"
              className={css.minimapClose}
              aria-label={t('game.board.minimapHide')}
              data-tip={t('game.board.minimapHide')}
              onClick={props.onToggleMinimap}
            >
              <Icon name="close" size={12} />
            </button>
          ) : null}
          <Minimap
            state={state}
            colours={props.colours}
            camera={camera}
            projection={proj}
            mapStyle={mapStyle}
            size={minimapSize(hostWidth)}
          />
        </div>
      )}
    </div>
  );
}

interface TipAt {
  readonly id: string;
  readonly x: number;
  readonly y: number;
  /** Over the point rather than under it: where a fingertip would hide it. */
  readonly above?: boolean;
}

/** How long a tap's tooltip stays, ms. */
const TAP_TIP_MS = 1800;

const ignore = () => undefined;

/** The unit under the mouse, described next to the pointer (BoardViewProps.unitTip). */
function UnitTip({
  render,
  register,
}: {
  render: (id: string) => ReactNode;
  register: (set: (tip: TipAt | null) => void) => void;
}) {
  const [tip, setTip] = useState<TipAt | null>(null);
  useLayoutEffect(() => {
    register((next) => {
      setTip((was) =>
        was === next ||
        (was &&
          next &&
          was.id === next.id &&
          was.x === next.x &&
          was.y === next.y &&
          was.above === next.above)
          ? was
          : next,
      );
    });
    return () => {
      register(ignore);
    };
  }, [register]);
  if (!tip) return null;
  return (
    <div
      className={css.unitTip}
      role="tooltip"
      data-testid="unit-tip"
      style={
        tip.above
          ? {
              left: `clamp(8px, ${tip.x}px - 7.5rem, calc(100% - 15rem - 8px))`,
              top: tip.y - 36,
              transform: 'translateY(-100%)',
            }
          : { left: `min(${tip.x + 14}px, calc(100% - 15rem))`, top: tip.y + 18 }
      }
    >
      {render(tip.id)}
    </div>
  );
}

/** A round map control; aria-disabled so its tooltip still answers. */
function MapButton({
  icon,
  label,
  keyHint,
  disabled = false,
  pressed,
  onClick,
}: {
  icon: IconName;
  label: string;
  keyHint?: string;
  disabled?: boolean;
  pressed?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={css.control}
      aria-label={label}
      aria-disabled={disabled || undefined}
      aria-pressed={pressed}
      data-tip={label}
      data-key={keyHint}
      onClick={() => {
        if (!disabled) onClick();
      }}
    >
      <Icon name={icon} />
    </button>
  );
}

/** About a quarter of the board's width, within 112–200 px. */
function minimapSize(boardWidth: number): number {
  return Math.round(Math.min(200, Math.max(112, boardWidth * 0.25)));
}

function webglAvailable(): boolean {
  if (typeof document === 'undefined' || typeof WebGLRenderingContext === 'undefined') return false;
  return true;
}
